import { HttpClient, HttpParams } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';

type DropTrailerLadingThemeMode = 'dark' | 'light';

interface DropTrailerLadingModalData {
  themeMode?: DropTrailerLadingThemeMode;
}

interface PoolPointClient {
  pool_point_client_uuid: string;
  pool_point_uuid: string;
  pool_point_client_name: string;
  pool_point_client_contact_uuid: string;
  pool_point_client_id: string;
  pool_point_client_scope: string;
  pool_point_name: string;
  contact_organization?: string;
  customer_contact_organization?: string;
  customer_legal_name?: string;
  store_count?: string | number;
  distribution_center_count?: string | number;
  address_locality?: string;
  address_region?: string;
}

interface DistributionCenter {
  customer_contact_distribution_center_uuid: string;
  customer_contact_uuid: string;
  distribution_center_contact_uuid: string;
  distribution_center_reference: string;
  contact_organization?: string;
  distribution_center_contact_organization?: string;
  contact_address_uuid?: string;
  address_locality?: string;
  address_region?: string;
  distribution_center_address_locality?: string;
  distribution_center_address_region?: string;
}

interface PoolPoint {
  pool_point_uuid: string;
  pool_point_name: string;
  pool_point_contact_uuid: string;
  pool_point_address_uuid: string;
  pool_point_id: string;
  pool_point_kitting_id: string;
  contact_organization?: string;
  pool_point_contact_organization?: string;
  address_locality?: string;
  address_region?: string;
}

interface DropTrailerCarrier {
  carrier_contact_uuid: string;
  carrier_name: string;
  contact_scac: string;
  us_dot_no: string;
  carrier_mc: string;
  contact_address_uuid?: string;
  address_locality?: string;
  address_region?: string;
}

interface AsnTrip {
  trip_list_uuid: string;
  trip_number: string;
  asn_number: string;
  external_bill_of_lading: string;
  total_cartons: string | number;
  reported_weight: string | number;
  lading_uuid: string | null;
  pool_point_uuid: string;
  pool_point_client_uuid: string;
  customer_contact_uuid: string;
  customer_contact_distribution_center_uuid: string;
  shipper_contact_uuid: string;
  consignee_contact_uuid: string;
  carrier_contact_uuid: string;
  trailer_id?: string;
  pool_point_name: string;
  pool_point_client_name: string;
  existing_child_lading_count: string | number;
  shipment_count: string | number;
}

@Component({
  selector: 'app-drop-trailer-lading-modal',
  templateUrl: './drop-trailer-lading-modal.component.html',
  styleUrls: ['./drop-trailer-lading-modal.component.scss'],
})
export class DropTrailerLadingModalComponent implements OnInit {
  private readonly apiUrl = '/api/drop_trailers/drop_trailers.php';
  private readonly defaultConsigneeContactUuid = 'ab4bddaf-cac9-4d92-8600-c01c021a7330';
  private readonly defaultConsigneeContactAddressUuid = '1fe20404-6b98-4c69-a1e8-57b16d4b30e0';
  readonly defaultConsigneeName = 'TBD';
  themeMode: DropTrailerLadingThemeMode = 'dark';

  form: FormGroup = this.fb.group({
    mode: ['spirit_kitting', Validators.required],
    lookupType: ['asn_number'],
    lookupValue: [''],
    tripListUuid: [''],
    poolPointClientContactUuid: [''],
    poolPointClientUuid: [''],
    distributionCenterUuid: ['', Validators.required],
    poolPointUuid: [''],
    carrierContactUuid: ['', Validators.required],
    ladingNumber: [''],
    carrierTrailer: [''],
    bookingNumber: [''],
    shipmentNumber: [''],
    sealNumber: [''],
    loadPieces: [''],
    loadWeight: [''],
  });

  poolPointClients: PoolPointClient[] = [];
  distributionCenters: DistributionCenter[] = [];
  poolPoints: PoolPoint[] = [];
  carriers: DropTrailerCarrier[] = [];
  tripMatches: AsnTrip[] = [];

  loadingLookups = false;
  loadingDistributionCenters = false;
  lookingUpTrip = false;
  submitting = false;
  lookupMessage = '';

  constructor(
    private http: HttpClient,
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<DropTrailerLadingModalComponent>,
    @Inject(MAT_DIALOG_DATA) data: DropTrailerLadingModalData | null,
    private toastr: ToastrService,
  ) {
    this.themeMode = data?.themeMode === 'light' ? 'light' : 'dark';
  }

  ngOnInit(): void {
    this.loadInitialLookups();
  }

  get selectedPoolPointClient(): PoolPointClient | undefined {
    const uuid = this.form.get('poolPointClientUuid')?.value;
    return this.poolPointClients.find((client) => client.pool_point_client_uuid === uuid);
  }

  get distinctPoolPointClients(): PoolPointClient[] {
    return this.uniqueBy(this.poolPointClients, 'pool_point_client_contact_uuid');
  }

  get selectedPoolPointClientContact(): PoolPointClient | undefined {
    const contactUuid = this.form.get('poolPointClientContactUuid')?.value;
    return this.distinctPoolPointClients.find((client) => client.pool_point_client_contact_uuid === contactUuid);
  }

  get selectedPoolPointClientIdOptions(): PoolPointClient[] {
    const contactUuid = this.form.get('poolPointClientContactUuid')?.value;
    if (!contactUuid) return [];
    return this.poolPointClients.filter((client) =>
      client.pool_point_client_contact_uuid === contactUuid && !!client.pool_point_client_id
    );
  }

  get selectedCustomerContactUuid(): string {
    return this.form.get('poolPointClientContactUuid')?.value
      || this.selectedPoolPointClient?.pool_point_client_contact_uuid
      || this.selectedDistributionCenter?.customer_contact_uuid
      || '';
  }

  get selectedDistributionCenter(): DistributionCenter | undefined {
    const uuid = this.form.get('distributionCenterUuid')?.value;
    return this.distributionCenters.find((center) => center.customer_contact_distribution_center_uuid === uuid);
  }

  get selectedPoolPoint(): PoolPoint | undefined {
    const uuid = this.form.get('poolPointUuid')?.value;
    return this.poolPoints.find((poolPoint) => poolPoint.pool_point_uuid === uuid);
  }

  get selectedCarrier(): DropTrailerCarrier | undefined {
    const uuid = this.form.get('carrierContactUuid')?.value;
    return this.carriers.find((carrier) => carrier.carrier_contact_uuid === uuid);
  }

  get isSpencerMode(): boolean {
    return this.form.get('mode')?.value === 'spencer_drop_trailer';
  }

  get modalTitle(): string {
    return this.isSpencerMode ? 'New Spencer Drop Trailer Lading' : 'New Drop Trailer Lading';
  }

  close(): void {
    this.dialogRef.close();
  }

  setMode(mode: 'spirit_kitting' | 'spencer_drop_trailer'): void {
    this.form.patchValue({ mode });
  }

  onPoolPointClientChange(): void {
    const contactUuid = this.form.get('poolPointClientContactUuid')?.value;

    this.form.patchValue({
      poolPointClientUuid: '',
      distributionCenterUuid: '',
      poolPointUuid: '',
    });

    this.distributionCenters = [];

    if (contactUuid) {
      this.loadDistributionCenters(contactUuid);
    }
  }

  onPoolPointClientIdChange(): void {
    const client = this.selectedPoolPointClient;
    this.form.patchValue({
      poolPointUuid: client?.pool_point_uuid || '',
    });
  }

  clearPoolPointClientSelection(): void {
    this.form.patchValue({
      poolPointClientContactUuid: '',
      poolPointClientUuid: '',
      poolPointUuid: '',
    });
  }

  lookupTrip(): void {
    const lookupValue = String(this.form.get('lookupValue')?.value || '').trim();
    const lookupType = this.form.get('lookupType')?.value;

    if (!lookupValue) {
      this.toastr.error('Enter an ASN or trip number before searching.');
      return;
    }

    this.lookingUpTrip = true;
    this.lookupMessage = '';
    this.tripMatches = [];

    const params: any = { request_type: 'asn_lookup' };
    params[lookupType === 'trip_number' ? 'trip_number' : 'asn_number'] = lookupValue;

    this.http.get<any>(this.apiUrl, { params }).subscribe({
      next: (response) => {
        this.lookingUpTrip = false;
        this.tripMatches = response?.trips || [];

        if (this.tripMatches.length === 0) {
          this.lookupMessage = 'No matching ASN or trip was found.';
          return;
        }

        if (this.tripMatches.length === 1) {
          this.applyTrip(this.tripMatches[0]);
        }
      },
      error: (error) => {
        this.lookingUpTrip = false;
        console.error('ASN lookup failed:', error);
        this.toastr.error('ASN lookup failed.');
      },
    });
  }

  applyTrip(trip: AsnTrip): void {
    if (trip.lading_uuid) {
      this.toastr.warning('This trip already has a main lading.');
    }

    this.form.patchValue({
      tripListUuid: trip.trip_list_uuid,
      poolPointClientContactUuid: trip.customer_contact_uuid || this.form.get('poolPointClientContactUuid')?.value,
      poolPointClientUuid: trip.pool_point_client_uuid || this.form.get('poolPointClientUuid')?.value,
      poolPointUuid: trip.pool_point_uuid || this.form.get('poolPointUuid')?.value,
      ladingNumber: trip.external_bill_of_lading || trip.asn_number || trip.trip_number || this.form.get('ladingNumber')?.value,
      carrierTrailer: trip.trailer_id || this.form.get('carrierTrailer')?.value,
      loadPieces: trip.total_cartons ?? this.form.get('loadPieces')?.value,
      loadWeight: trip.reported_weight ?? this.form.get('loadWeight')?.value,
      carrierContactUuid: trip.carrier_contact_uuid || this.form.get('carrierContactUuid')?.value,
    });

    const client = this.selectedPoolPointClient;
    const customerContactUuid = trip.customer_contact_uuid || client?.pool_point_client_contact_uuid || this.selectedPoolPointClientContact?.pool_point_client_contact_uuid;

    if (customerContactUuid) {
      this.loadDistributionCenters(customerContactUuid, trip);
    }
  }

  submit(): void {
    if (this.form.invalid || this.submitting) {
      this.form.markAllAsTouched();
      this.toastr.error('Complete the required drop trailer fields.');
      return;
    }

    if (this.isSpencerMode && !this.form.get('tripListUuid')?.value) {
      this.toastr.error('Select an ASN or trip for Spencer drop trailers.');
      return;
    }

    const client = this.selectedPoolPointClient;
    const distributionCenter = this.selectedDistributionCenter;
    const poolPoint = this.selectedPoolPoint;
    const carrier = this.selectedCarrier;
    const consigneeContactUuid = poolPoint?.pool_point_contact_uuid || this.defaultConsigneeContactUuid;
    const consigneeContactAddressUuid = poolPoint?.pool_point_address_uuid || this.defaultConsigneeContactAddressUuid;
    const consigneeName = poolPoint?.contact_organization || poolPoint?.pool_point_name || this.defaultConsigneeName;

    if (!distributionCenter || !carrier) {
      this.toastr.error('One or more selected records could not be resolved.');
      return;
    }

    const payload = {
      action: 'create_drop_trailer_ladings',
      mode: this.form.get('mode')?.value,
      pool_point_client_uuid: client?.pool_point_client_uuid || null,
      pool_point_uuid: poolPoint?.pool_point_uuid || null,
      customer_contact_distribution_center_uuid: distributionCenter.customer_contact_distribution_center_uuid,
      carrier_contact_uuid: carrier.carrier_contact_uuid,
      trip_list_uuid: this.form.get('tripListUuid')?.value || undefined,
      lading: {
        lading_number: String(this.form.get('ladingNumber')?.value || '').trim(),
        carrier_trailer: this.nullIfBlank(this.form.get('carrierTrailer')?.value),
        booking_number: this.nullIfBlank(this.form.get('bookingNumber')?.value),
        shipment_number: this.nullIfBlank(this.form.get('shipmentNumber')?.value),
        seal_number: this.nullIfBlank(this.form.get('sealNumber')?.value),
        load_pieces: this.nullIfBlank(this.form.get('loadPieces')?.value),
        load_weight: this.nullIfBlank(this.form.get('loadWeight')?.value),
        customer_contact_uuid: this.selectedCustomerContactUuid || null,
        shipper_contact_uuid: distributionCenter.distribution_center_contact_uuid,
        consignee_contact_uuid: consigneeContactUuid,
        consignee_contact_address_uuid: consigneeContactAddressUuid,
        consignee_name: consigneeName,
        carrier_contact_uuid: carrier.carrier_contact_uuid,
      },
    };

    this.submitting = true;

    this.http.post<any>(this.apiUrl, payload).subscribe({
      next: (response) => {
        this.submitting = false;

        if (!this.isSuccess(response)) {
          this.toastr.error(response?.message || 'Drop trailer lading could not be created.');
          return;
        }

        this.toastr.success('Drop trailer lading created.');
        this.dialogRef.close({ refreshGrid: true, ladingUuid: response.lading_uuid });
      },
      error: (error) => {
        this.submitting = false;
        console.error('Create drop trailer lading failed:', error);
        this.toastr.error('Network error creating drop trailer lading.');
      },
    });
  }

  displayLocation(record: { address_locality?: string; address_region?: string } | null | undefined): string {
    const parts = [record?.address_locality, record?.address_region].filter(Boolean);
    return parts.length ? parts.join(', ') : 'Location not set';
  }

  private loadInitialLookups(): void {
    this.loadingLookups = true;

    forkJoin({
      clients: this.getLookup('pool_point_clients'),
      poolPoints: this.getLookup('pool_points'),
      carriers: this.getLookup('drop_trailer_carriers'),
    }).subscribe({
      next: ({ clients, poolPoints, carriers }) => {
        this.loadingLookups = false;
        this.poolPointClients = this.uniqueBy(this.asArray<PoolPointClient>(clients).map((client) => this.normalizePoolPointClient(client)), 'pool_point_client_uuid');
        this.poolPoints = this.uniqueBy(this.asArray<PoolPoint>(poolPoints).map((poolPoint) => this.normalizePoolPoint(poolPoint)), 'pool_point_uuid');
        this.carriers = this.uniqueBy(this.asArray<DropTrailerCarrier>(carriers?.carriers || carriers).map((carrier) => this.normalizeCarrier(carrier)), 'carrier_contact_uuid');
      },
      error: (error) => {
        this.loadingLookups = false;
        console.error('Drop trailer lookup load failed:', error);
        this.toastr.error('Drop trailer lookups could not be loaded.');
      },
    });
  }

  private loadDistributionCenters(customerContactUuid: string, trip?: AsnTrip): void {
    this.loadingDistributionCenters = true;

    this.getLookup('distribution_centers', { customer_contact_uuid: customerContactUuid }).subscribe({
      next: (response) => {
        this.loadingDistributionCenters = false;
        this.distributionCenters = this.uniqueBy(
          this.asArray<DistributionCenter>(response).map((center) => this.normalizeDistributionCenter(center)),
          'customer_contact_distribution_center_uuid'
        );

        if (trip) {
          const match = this.distributionCenters.find((center) =>
            center.customer_contact_distribution_center_uuid === trip.customer_contact_distribution_center_uuid
            || center.distribution_center_contact_uuid === trip.shipper_contact_uuid
          );

          if (match) {
            this.form.patchValue({ distributionCenterUuid: match.customer_contact_distribution_center_uuid });
          }
        }
      },
      error: (error) => {
        this.loadingDistributionCenters = false;
        console.error('Distribution center lookup failed:', error);
        this.toastr.error('Distribution centers could not be loaded.');
      },
    });
  }

  private getLookup(requestType: string, extraParams: Record<string, string> = {}) {
    let params = new HttpParams().set('request_type', requestType);
    Object.entries(extraParams).forEach(([key, value]) => {
      params = params.set(key, value);
    });

    return this.http.get<any>(this.apiUrl, { params });
  }

  private asArray<T>(value: any): T[] {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.data)) return value.data;
    if (Array.isArray(value?.trips)) return value.trips;
    return [];
  }

  private normalizePoolPointClient(client: PoolPointClient): PoolPointClient {
    return {
      ...client,
      contact_organization: client.contact_organization || client.customer_contact_organization || client.pool_point_client_name,
    };
  }

  private normalizeDistributionCenter(center: DistributionCenter): DistributionCenter {
    return {
      ...center,
      contact_organization: center.contact_organization || center.distribution_center_contact_organization || center.distribution_center_reference,
      address_locality: center.address_locality || center.distribution_center_address_locality,
      address_region: center.address_region || center.distribution_center_address_region,
    };
  }

  private normalizePoolPoint(poolPoint: PoolPoint): PoolPoint {
    return {
      ...poolPoint,
      contact_organization: poolPoint.contact_organization || poolPoint.pool_point_contact_organization || poolPoint.pool_point_name,
    };
  }

  private normalizeCarrier(carrier: DropTrailerCarrier): DropTrailerCarrier {
    return {
      ...carrier,
      carrier_contact_uuid: carrier.carrier_contact_uuid || (carrier as any).contact_uuid,
      carrier_name: carrier.carrier_name || (carrier as any).contact_organization || (carrier as any).legal_name,
    };
  }

  private uniqueBy<T extends Record<string, any>>(rows: T[], key: string): T[] {
    const seen = new Set<string>();
    return rows.filter((row) => {
      const value = row?.[key];
      if (!value || seen.has(value)) return false;
      seen.add(value);
      return true;
    });
  }

  private isSuccess(response: any): boolean {
    return response?.success === true;
  }

  private nullIfBlank(value: any): any {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string' && value.trim() === '') return null;
    return value;
  }
}
